import { useEffect, useRef, useState } from "react";
import { api, getTrialId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TemplatePicker } from "@/components/TemplatePreview";
import { ProcessingEstimate } from "@/components/ProcessingEstimate";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { UploadCloud, Film, Sparkles, Loader2, AlertTriangle, Gem, Scale, Layers } from "lucide-react";
import { uploadVideoInParts, uploadErrorMessage, MAX_VIDEO_BYTES } from "@/lib/upload";
import { toast } from "sonner";

const SELECTION_MODES = [
  { id: "quality", label: "Quality", Icon: Gem, accent: "cyan", summary: "Only ready-to-post clips", detail: "Strict gate (70+). Count is a maximum; weak or unfinished moments are dropped." },
  { id: "balanced", label: "Balanced", Icon: Scale, accent: "fuchsia", summary: "Strong clips plus solid extras", detail: "Moderate gate (58+). Aims toward your count; incomplete moments still excluded." },
  { id: "quantity", label: "Quantity", Icon: Layers, accent: "amber", summary: "As many complete clips as possible", detail: "Lenient gate (45+). Mixed quality by design; check scores before posting." },
];
const ACCENTS = {
  cyan: "border-cyan-400 bg-cyan-400/10 text-cyan-300",
  fuchsia: "border-fuchsia-400 bg-fuchsia-400/10 text-fuchsia-300",
  amber: "border-amber-400 bg-amber-400/10 text-amber-300",
};

function SelectionModePicker({ value, onChange, disabled }) {
  return (
    <div data-testid="selection-mode-section" className="rounded-2xl border border-surface-800 bg-surface-950 p-4 space-y-3">
      <Label className="text-xs uppercase tracking-widest text-copy-400">Clip generation method</Label>
      <div role="radiogroup" className="grid sm:grid-cols-3 gap-3">
        {SELECTION_MODES.map(({ id, label, Icon, accent, summary, detail }) => {
          const active = value === id;
          return (
            <button key={id} type="button" role="radio" aria-checked={active} disabled={disabled}
              data-testid={`selection-mode-${id}`} onClick={() => onChange(id)}
              className={`text-left rounded-xl border p-3 transition-colors duration-200 disabled:opacity-60 ${active ? ACCENTS[accent] : "border-surface-800 bg-surface-900 text-copy-300 hover:border-surface-600"}`}>
              <div className="flex items-center gap-2 text-sm font-semibold"><Icon className="h-4 w-4" /> {label}</div>
              <p className="mt-1 text-xs font-medium">{summary}</p>
              <p className="mt-1 text-[11px] leading-relaxed text-copy-500">{detail}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const fileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export function UploadZone({ onUploaded, afterDropzone }) {
  const { user, openAuth } = useAuth();
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const uploadButtonRef = useRef(null);
  const settingsButtonRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templateId, setTemplateId] = useState("tmpl_hormozi");
  const [captions, setCaptions] = useState(true);
  const [faceTrack, setFaceTrack] = useState(true);
  const [clipCount, setClipCount] = useState(12);
  const [selectionMode, setSelectionMode] = useState("quality");
  const [maxSeconds, setMaxSeconds] = useState("180");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState("");
  const [savedBytes, setSavedBytes] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const uploadSession = useRef(null);
  const [estimate, setEstimate] = useState(null);
  const [warning, setWarning] = useState(null);
  const inputRef = useRef(null);
  const uploadStarted = useRef(false);
  const estimateCurrent = estimate?.file === file && estimate?.clipCount === Number(clipCount)
    && estimate?.maxSeconds === maxSeconds && estimate?.faceTrack === faceTrack
    && estimate?.captions === captions && estimate?.userId === user?.id;

  useEffect(() => {
    setTemplatesLoading(true);
    api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => toast.error("Could not load styles. Please reopen this panel.")).finally(() => setTemplatesLoading(false));
  }, [user?.id]);

  const pick = (f) => {
    if (!f || uploading) return;
    if (!f.type.startsWith("video/") && !/\.(mp4|mov|m4v|webm|mkv|avi)$/i.test(f.name)) {
      toast.error("Please choose a video file (MP4, MOV, WEBM).");
      return;
    }
    if (f.size > MAX_VIDEO_BYTES || f.size === 0) {
      setUploadError(f.size === 0 ? "This file is empty. Choose a video file." : "Choose a video no larger than 1 GB.");
      setFile(null);
      return;
    }
    setUploadError("");
    setSavedBytes(0);
    uploadSession.current = null;
    setEstimate(null);
    setWarning(null);
    setFile(f);
    if (afterDropzone) setSettingsOpen(true);
  };

  const start = async (count) => {
    if (!file || uploadStarted.current) return;
    uploadStarted.current = true;
    setUploading(true);
    setProgress(0);
    setUploadError("");
    try {
      const settings = {
        template_id: templateId, caption_enabled: captions, face_track: faceTrack,
        clips_requested: Number(count), max_clip_seconds: Number(maxSeconds), selection_mode: selectionMode, trial_id: getTrialId(),
      };
      const signature = JSON.stringify({ ...settings, user_id: user?.id });
      if (uploadSession.current?.file !== file || uploadSession.current?.signature !== signature) {
        uploadSession.current = { id: crypto.randomUUID().replaceAll("-", ""), file, signature };
      }
      const data = await uploadVideoInParts(file, settings, uploadSession.current, (update) => {
        if (update.progress !== undefined) setProgress(update.progress);
        if (update.savedBytes !== undefined) setSavedBytes(update.savedBytes);
        setUploadPhase(update.phase);
      });
      toast.success("Video saved — AI is on it!");
      onUploaded(data.project_id);
    } catch (err) {
      const status = err.response?.status;
      const message = uploadErrorMessage(err);
      uploadStarted.current = false;
      setUploading(false);
      setUploadError(message);
      if (status === 401) {
        toast.error(message);
        openAuth("login");
        return;
      }
      if (status === 403 && !user) {
        // Free trial exhausted — guide the user into creating an account instead of a bare error.
        toast.info("You've used all 10 free trial clips — create a free account to keep making clips!", { duration: 6000 });
        openAuth("register");
        return;
      }
      toast.error(message);
    }
  };

  const requestStart = () => {
    if (!estimateCurrent || estimate.pending || uploading) return;
    const requested = Number(clipCount);
    if (estimate.feasible_clips && requested > estimate.feasible_clips) {
      setWarning({ requested, recommended: estimate.recommended_clips || estimate.feasible_clips,
        seconds: Math.round(estimate.duration_seconds), allowance: estimate.clips_budgeted });
      return;
    }
    start(requested);
  };

  const confirmCount = (count) => {
    setWarning(null);
    setClipCount(count);
    start(count);
  };

  const selectedFile = file && (
    <div className="min-w-0 max-w-full">
      <p data-testid="selected-video-name" className="font-display text-lg font-semibold break-words">{file.name}</p>
      <p data-testid="selected-video-size" className="text-sm text-copy-500 mt-1">{fileSize(file.size)} · Up to 1 GB · Uploaded in small, retryable parts</p>
    </div>
  );

  const uploadIntro = (
      <div data-testid="upload-intro-layout" className={afterDropzone ? "min-w-0 space-y-4" : undefined}>
        <input ref={inputRef} type="file" accept="video/*" hidden
          data-testid="upload-file-input" disabled={uploading} onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
        {afterDropzone ? (
          <div data-testid="homepage-upload-controls" className="flex min-w-0 flex-col items-center space-y-3 text-center">
            <Button ref={uploadButtonRef} type="button" data-testid="upload-files-button" disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className="h-12 rounded-full bg-cyan-500 px-6 font-semibold text-black transition-[background-color,transform] hover:bg-cyan-400 active:scale-[0.98]">
              <UploadCloud aria-hidden="true" className="h-5 w-5" /> Upload files
            </Button>
            {selectedFile || <p data-testid="upload-file-guidance" className="text-sm text-copy-500">Choose one video · MP4, MOV, WEBM · Up to 1 GB</p>}
            {file && <Button ref={settingsButtonRef} type="button" data-testid="edit-upload-settings-button" variant="outline" disabled={uploading}
              onClick={() => setSettingsOpen(true)} className="rounded-full border-surface-700 text-copy-300">Edit settings</Button>}
            {uploadError && !settingsOpen && <p data-testid="homepage-upload-error" role="alert" className="text-sm text-amber-500">{uploadError}</p>}
          </div>
        ) : (
          <div
            data-testid="upload-video-zone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
            onClick={() => !uploading && inputRef.current?.click()}
            className={`relative cursor-pointer rounded-3xl border-2 border-dashed p-10 sm:p-14 text-center transition-colors ${
              dragging ? "border-cyan-500 bg-cyan-500/5" : "border-surface-700 hover:border-cyan-500/60 hover:bg-cyan-500/[0.03]"
            }`}
          >
            <div className="mx-auto h-16 w-16 rounded-2xl bg-surface-900 border border-surface-800 flex items-center justify-center mb-5">
              {file ? <Film className="h-7 w-7 text-cyan-400" /> : <UploadCloud className="h-7 w-7 text-cyan-400" />}
            </div>
            {selectedFile || (
              <div>
                <p className="font-display text-xl font-bold">Drop your long-form video here</p>
                <p className="text-sm text-copy-500 mt-2">or click to browse · MP4, MOV, WEBM · Up to 1 GB</p>
              </div>
            )}
          </div>
        )}
        {afterDropzone && <div data-testid="upload-workflow-column" className="min-w-0">{afterDropzone}</div>}
      </div>
  );

  const settingsPanel = (
    <div data-testid="upload-settings-fields" className="min-w-0 space-y-5">
      <div data-testid="upload-style-section" className="rounded-2xl border border-surface-800 bg-surface-950 p-4">
        <div className="flex items-center justify-between gap-3 mb-4">
          <Label className="text-xs uppercase tracking-widest text-copy-400">Choose your content style</Label>
          <span data-testid="style-count" className="text-xs text-cyan-400">5 signature looks</span>
        </div>
        <TemplatePicker templates={templates} value={templateId} onChange={setTemplateId} prefix="upload-style" disabled={uploading} loading={templatesLoading} />
      </div>
      <div>
        <div className="rounded-2xl border border-surface-800 bg-surface-950 p-4 flex items-center justify-between">
          <div>
            <Label className="text-xs uppercase tracking-widest text-copy-500">Auto captions</Label>
            <p className="text-sm text-copy-400 mt-1">Caption motion follows your style. Headlines are included separately.</p>
          </div>
          <Switch data-testid="captions-switch" checked={captions} onCheckedChange={setCaptions} disabled={uploading}
            className="data-[state=checked]:bg-cyan-500" />
        </div>
      </div>

      <div className="rounded-2xl border border-surface-800 bg-surface-950 p-4 flex items-center justify-between">
        <div>
          <Label className="text-xs uppercase tracking-widest text-copy-400">Automatic speaker framing</Label>
          <p className="text-sm text-copy-400 mt-1">On by default. Close framing resets at camera cuts; uncertain pairs stay in two large panels.</p>
          <p data-testid="framing-limitation" className="text-xs text-copy-500 mt-2">If the speaker leaves the source shot, we preserve that shot rather than invent missing footage.</p>
        </div>
        <Switch data-testid="facetrack-switch" checked={faceTrack} onCheckedChange={setFaceTrack} disabled={uploading}
          className="data-[state=checked]:bg-fuchsia-500" />
      </div>

      <SelectionModePicker value={selectionMode} onChange={setSelectionMode} disabled={uploading} />
      <div className="grid sm:grid-cols-2 gap-4 rounded-2xl border border-surface-800 bg-surface-950 p-4">
        <div className="space-y-2">
          <Label htmlFor="clips-requested" className="text-xs uppercase tracking-widest text-copy-400">{selectionMode === "quality" ? "Maximum clips" : "Target clips"}</Label>
          <Input id="clips-requested" data-testid="clips-requested-input" type="number" min={1} max={40} value={clipCount} onChange={e => setClipCount(e.target.value)} disabled={uploading} className="bg-surface-900 border-surface-700" />
          <p data-testid="clip-count-hint" className="text-xs text-copy-500">Up to 40, subject to your remaining allowance. More clips take longer to render.</p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-copy-400">Clip length</Label>
          <Select value={maxSeconds} onValueChange={setMaxSeconds} disabled={uploading}>
            <SelectTrigger data-testid="clip-length-select" className="bg-surface-900 border-surface-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-surface-900 border-surface-700 text-foreground">
              <SelectItem data-testid="clip-length-180" value="180">Auto · up to 3 minutes</SelectItem>
              <SelectItem data-testid="clip-length-90" value="90">Up to 90 seconds</SelectItem>
              <SelectItem data-testid="clip-length-60" value="60">Up to 60 seconds</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-copy-500">Keep the full explanation instead of cutting it at an arbitrary minute.</p>
        </div>
      </div>
      <p data-testid="selection-quality-note" className="text-xs leading-relaxed text-copy-400">
        {selectionMode === "quality" && "Quality first: this count is a maximum, not a target. We only select strong, complete, distinct moments, so you may receive fewer clips."}
        {selectionMode === "balanced" && "Balanced: we aim toward this count with a mix of standout and solid clips, but still skip anything incomplete or clearly weak."}
        {selectionMode === "quantity" && "Quantity: we find as many complete moments as possible up to this count. Quality will vary, so review each clip's score before posting."}
        {" "}Only saved clips count toward your allowance.
      </p>
      <ProcessingEstimate file={file} clipCount={clipCount} maxSeconds={maxSeconds} faceTrack={faceTrack} captions={captions} userId={user?.id} onEstimate={setEstimate} />
      {uploadError && <div data-testid="upload-error" role="alert" className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-4 text-sm text-amber-400">
        <p data-testid="upload-error-message">{uploadError}</p>
        {file && <p data-testid="upload-retry-hint" className="mt-2 text-xs text-copy-400">Keep this page open and keep the same file and clip settings to reuse saved parts. Changing settings or selecting the file again starts a new upload.</p>}
      </div>}
      {uploading ? (
        <div className="rounded-2xl border border-surface-800 bg-surface-950 p-5" data-testid="upload-progress" role="status">
          <div className="flex items-center gap-3 text-sm text-copy-300 mb-3">
            <Loader2 className="h-4 w-4 animate-spin text-cyan-400" />
            <span data-testid="upload-phase">{uploadPhase}</span>
          </div>
          <Progress data-testid="upload-saved-progress" value={progress} className="h-2 bg-surface-800" />
          <p data-testid="upload-saved-bytes" className="mt-3 text-xs text-copy-400">{fileSize(savedBytes)} / {fileSize(file.size)} saved · {progress}%</p>
          <p data-testid="upload-stay-open" className="mt-1 text-xs text-copy-500">Keep this page open until all parts are saved. Interrupted parts retry automatically; after that you can close the page and clips are created in the background.</p>
        </div>
      ) : (
        <Button data-testid="generate-clips-btn" onClick={requestStart} disabled={!file || !estimateCurrent || estimate?.pending || (estimate?.duration_seconds > 0 && estimate.duration_seconds < 12) || Number(clipCount) < 1 || Number(clipCount) > 40 || !Number.isInteger(Number(clipCount))}
          className="w-full h-14 rounded-full bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-base disabled:opacity-40">
          {file && (!estimateCurrent || estimate?.pending) ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Checking video…</> : <><Sparkles className="h-5 w-5 mr-2" /> {uploadError ? "Retry upload" : "Generate viral clips"}</>}
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="upload-zone-wrap">
      {uploadIntro}
      {afterDropzone ? (
        <Dialog open={settingsOpen} onOpenChange={(open) => { if (!uploading && !warning) setSettingsOpen(open); }}>
          <DialogContent data-testid="upload-settings-dialog"
            className="flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-4xl flex-col overflow-hidden rounded-2xl border-surface-700 bg-surface-950 p-0 text-foreground"
            closeButtonProps={{ "data-testid": "upload-settings-close", disabled: uploading || Boolean(warning) }}
            onEscapeKeyDown={(event) => { if (uploading || warning) event.preventDefault(); }}
            onInteractOutside={(event) => { if (uploading || warning) event.preventDefault(); }}
            onCloseAutoFocus={(event) => { event.preventDefault(); (settingsButtonRef.current || uploadButtonRef.current)?.focus(); }}>
            <DialogHeader className="shrink-0 border-b border-surface-800 p-5 pr-12 text-left sm:p-6 sm:pr-14">
              <DialogTitle data-testid="upload-settings-title" className="font-display text-lg font-bold">Create your clips</DialogTitle>
              <DialogDescription data-testid="upload-settings-description" className="text-sm text-copy-400">
                {uploading ? "Your video is uploading. Keep this page open until all parts are saved." : "Choose your look and clip settings. Uploading starts only when you select Generate."}
              </DialogDescription>
            </DialogHeader>
            <div data-testid="upload-settings-scroll-area" className="min-h-0 space-y-5 overflow-y-auto overscroll-contain p-4 sm:p-6">
              <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p data-testid="settings-selected-video-name" className="font-display font-semibold [overflow-wrap:anywhere]">{file?.name || "Choose a video to continue"}</p>
                  {file && <p data-testid="settings-selected-video-size" className="mt-1 text-xs text-copy-500">{fileSize(file.size)} · Your file and settings stay selected when this popup closes</p>}
                </div>
                <Button type="button" data-testid="settings-change-video-button" variant="outline" disabled={uploading}
                  onClick={() => inputRef.current?.click()} className="shrink-0 self-start rounded-full border-surface-700">Change video</Button>
              </div>
              {settingsPanel}
            </div>
          </DialogContent>
        </Dialog>
      ) : settingsPanel}
      <AlertDialog open={Boolean(warning)} onOpenChange={(open) => !open && setWarning(null)}>
        <AlertDialogContent data-testid="clip-feasibility-dialog" className="w-[calc(100%-2rem)] rounded-2xl border-amber-400/30 bg-surface-950 text-copy-100">
          <AlertDialogHeader className="text-left">
            <AlertTriangle aria-hidden="true" className="h-6 w-6 text-amber-400 mb-2" />
            <AlertDialogTitle data-testid="clip-feasibility-title">This short source may yield fewer clips</AlertDialogTitle>
            <AlertDialogDescription data-testid="clip-feasibility-description" className="text-copy-300 leading-relaxed">
              Your maximum is {warning?.requested} clips for this {Math.floor((warning?.seconds || 0) / 60)}m {(warning?.seconds || 0) % 60}s video. Keep this maximum, or lower it to {warning?.recommended} and start. Either way, only strong, complete moments are selected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <p data-testid="clip-feasibility-basis" className="text-xs leading-relaxed text-copy-400">The suggestion allows roughly 30 seconds of source per clip; it is not a guaranteed count. We never add weak or repeated moments just to fill your maximum.</p>
          {warning?.allowance < warning?.requested && <p data-testid="clip-feasibility-allowance" className="text-xs text-amber-300">Your current allowance still limits this upload to {warning.allowance} clips.</p>}
          <AlertDialogFooter className="gap-2 sm:space-x-0">
            <AlertDialogCancel data-testid="clip-feasibility-continue" onClick={() => confirmCount(warning.requested)} className="border-surface-700 bg-surface-900 hover:bg-surface-800 hover:text-foreground">Keep maximum</AlertDialogCancel>
            <AlertDialogAction data-testid="clip-feasibility-reduce" onClick={() => confirmCount(warning.recommended)} className="bg-cyan-500 text-black hover:bg-cyan-400">Lower maximum</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
